import { useEffect, useMemo, useState } from 'react';
import { usePatientList } from '@/features/patients';
import { useInvoiceList } from '@/features/billing';
import { useAppointmentList } from '@/features/appointments';
import type { InvoiceSummary } from '@/features/billing';
import type { Patient } from '@/features/patients';
import type { Appointment } from '@/features/appointments';
import {
  GLOBAL_SEARCH_APPOINTMENT_LIMIT,
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_INVOICE_LIMIT,
  GLOBAL_SEARCH_MIN_CHARS,
  GLOBAL_SEARCH_PATIENT_LIMIT,
  GLOBAL_SEARCH_QUICK_ACTIONS,
  type GlobalSearchAppointmentHit,
  type GlobalSearchHit,
  type GlobalSearchInvoiceHit,
  type GlobalSearchPatientHit,
  type GlobalSearchRecentHit,
} from '../domain/global-search.types';
import {
  parseSearchCommandQuery,
  type SearchCommandMode,
} from '../domain/parse-search-command';
import { filterCommandsByTerm, filterCommandsForRole } from '../domain/search-commands';

function toPatientHit(patient: Patient): GlobalSearchPatientHit {
  return {
    kind: 'patient',
    id: patient.id,
    name: patient.name,
    phone: patient.phone ?? patient.contacts?.primaryPhone ?? undefined,
    patientDisplayId: patient.patientDisplayId,
    bloodGroup: patient.bloodGroup,
    href: `/patients/${patient.id}`,
  };
}

function toAppointmentHit(appointment: Appointment): GlobalSearchAppointmentHit {
  return {
    kind: 'appointment',
    id: appointment.id,
    patientName: appointment.patientName?.trim() || 'Patient',
    providerName: appointment.providerName,
    scheduledAt: appointment.scheduledAt,
    status: appointment.status,
    href: `/appointments/${appointment.id}`,
  };
}

function toInvoiceHit(invoice: InvoiceSummary): GlobalSearchInvoiceHit {
  return {
    kind: 'invoice',
    id: invoice.id,
    displayNumber: invoice.displayNumber?.trim() || invoice.id.slice(0, 8),
    patientName: invoice.patientName,
    patientDisplayId: invoice.patientDisplayId,
    status: invoice.status,
    totalPaise: invoice.totalPaise,
    href: `/billing/invoices/${invoice.id}`,
  };
}

export interface UseGlobalSearchResult {
  query: string;
  debounced: string;
  mode: SearchCommandMode;
  searchTerm: string;
  hits: GlobalSearchHit[];
  patientHits: GlobalSearchPatientHit[];
  appointmentHits: GlobalSearchAppointmentHit[];
  invoiceHits: GlobalSearchInvoiceHit[];
  isIdle: boolean;
  isBelowMinChars: boolean;
  isFetching: boolean;
  isError: boolean;
  hasResults: boolean;
}

/**
 * Debounced patients + appointments + invoices fan-out with command-palette modes.
 */
export function useGlobalSearch(
  query: string,
  open: boolean,
  recents: GlobalSearchRecentHit[] = [],
  role?: string | null,
): UseGlobalSearchResult {
  const [debouncedRaw, setDebouncedRaw] = useState(query.trim());

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedRaw(query.trim()), GLOBAL_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const parsedLive = useMemo(() => parseSearchCommandQuery(query), [query]);
  const parsedDebounced = useMemo(() => parseSearchCommandQuery(debouncedRaw), [debouncedRaw]);

  const mode = parsedLive.mode;
  const searchTerm = parsedDebounced.term;
  const isIdle = query.trim().length === 0;
  const isCommandMode = mode === 'command';

  const isBelowMinChars =
    !isIdle &&
    !isCommandMode &&
    parsedLive.term.length > 0 &&
    parsedLive.term.length < GLOBAL_SEARCH_MIN_CHARS;

  const entityEnabled =
    open &&
    !isCommandMode &&
    searchTerm.length >= GLOBAL_SEARCH_MIN_CHARS &&
    (mode === 'all' || mode === 'patient' || mode === 'appointment' || mode === 'invoice');

  const patientsEnabled = entityEnabled && (mode === 'all' || mode === 'patient');
  const appointmentsEnabled = entityEnabled && (mode === 'all' || mode === 'appointment');
  const invoicesEnabled = entityEnabled && (mode === 'all' || mode === 'invoice');

  const patientsQuery = usePatientList(
    { search: searchTerm || undefined, limit: GLOBAL_SEARCH_PATIENT_LIMIT },
    { enabled: patientsEnabled, keepPrevious: true },
  );
  const appointmentsQuery = useAppointmentList(
    {
      search: searchTerm || undefined,
      limit: GLOBAL_SEARCH_APPOINTMENT_LIMIT,
      page: 1,
      sortBy: 'scheduled_at',
      sortOrder: 'desc',
    },
    { enabled: appointmentsEnabled, keepPrevious: true },
  );
  const invoicesQuery = useInvoiceList(
    { search: searchTerm || undefined, page: 1 },
    { enabled: invoicesEnabled, keepPrevious: true },
  );

  const roleCommands = useMemo(() => filterCommandsForRole(role), [role]);
  const idleActions = useMemo(
    () => filterCommandsForRole(role, GLOBAL_SEARCH_QUICK_ACTIONS),
    [role],
  );

  const patientHits = useMemo(
    () => (patientsEnabled ? (patientsQuery.data?.data ?? []).map(toPatientHit) : []),
    [patientsEnabled, patientsQuery.data?.data],
  );

  const appointmentHits = useMemo(
    () => (appointmentsEnabled ? (appointmentsQuery.data?.data ?? []).map(toAppointmentHit) : []),
    [appointmentsEnabled, appointmentsQuery.data?.data],
  );

  const invoiceHits = useMemo(
    () =>
      invoicesEnabled
        ? (invoicesQuery.data?.items ?? []).slice(0, GLOBAL_SEARCH_INVOICE_LIMIT).map(toInvoiceHit)
        : [],
    [invoicesEnabled, invoicesQuery.data?.items],
  );

  const hits = useMemo((): GlobalSearchHit[] => {
    if (isIdle) {
      return [...recents, ...idleActions];
    }

    if (isCommandMode) {
      return filterCommandsByTerm(roleCommands, parsedLive.term);
    }

    if (isBelowMinChars || parsedLive.term.length < GLOBAL_SEARCH_MIN_CHARS) {
      return [];
    }

    if (!entityEnabled && parsedDebounced.term.length < GLOBAL_SEARCH_MIN_CHARS) {
      return [];
    }

    const next: GlobalSearchHit[] = [...patientHits, ...appointmentHits, ...invoiceHits];
    if (appointmentHits.length > 0) {
      next.push({
        kind: 'viewAllAppointments',
        id: 'view-all-appointments',
        label: `View appointments for “${searchTerm}”`,
        href: `/appointments/list?search=${encodeURIComponent(searchTerm)}`,
      });
    }
    if (invoiceHits.length > 0) {
      next.push({
        kind: 'viewAllInvoices',
        id: 'view-all-invoices',
        label: `View all invoices for “${searchTerm}”`,
        href: `/billing/invoices?search=${encodeURIComponent(searchTerm)}`,
      });
    }
    return next;
  }, [
    isIdle,
    recents,
    idleActions,
    isCommandMode,
    roleCommands,
    parsedLive.term,
    isBelowMinChars,
    entityEnabled,
    parsedDebounced.term,
    patientHits,
    appointmentHits,
    invoiceHits,
    searchTerm,
  ]);

  const isFetching =
    (patientsEnabled && patientsQuery.isFetching) ||
    (appointmentsEnabled && appointmentsQuery.isFetching) ||
    (invoicesEnabled && invoicesQuery.isFetching);

  const isError =
    (patientsEnabled && patientsQuery.isError) ||
    (appointmentsEnabled && appointmentsQuery.isError) ||
    (invoicesEnabled && invoicesQuery.isError);

  return {
    query,
    debounced: searchTerm,
    mode,
    searchTerm,
    hits,
    patientHits,
    appointmentHits,
    invoiceHits,
    isIdle,
    isBelowMinChars,
    isFetching,
    isError,
    hasResults:
      isCommandMode
        ? hits.length > 0
        : patientHits.length > 0 || appointmentHits.length > 0 || invoiceHits.length > 0,
  };
}
